import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SEARCH_MIN_CHARS, SORT_FIELDS } from "../contract";

/** Array filters travel as repeated params (`stage=lead&stage=active`). */
export const ARRAY_FILTERS = Object.freeze(["stage", "tags", "source"]);
/** Single-value filters. `purchased_*` / `created_*` arrive from dashboard drill-downs. */
export const SCALAR_FILTERS = Object.freeze([
  "q", "inactive_days", "pack_status", "consent",
  "created_from", "created_to", "purchased_kind", "purchased_from", "purchased_to",
]);
/** The deep-link filters that render as removable chips rather than dropdowns. */
export const DEEP_LINK_FILTERS = Object.freeze(["created_from", "created_to", "purchased_kind", "purchased_from", "purchased_to"]);

const DEFAULT_SORT = { field: "name", dir: "asc" };

/**
 * The Clients list filters, held in the ADDRESS (`?stage=active&tags=yoga&sort=…`), so a
 * filtered list can be bookmarked, shared with a helper, reached from a dashboard card
 * and restored by Back. Writes replace the history entry: typing a search must not fill
 * the Back stack.
 *
 * `query` is what the list endpoint receives: `q` only from 3 characters (spec: "Live
 * after 3 characters"), empty values dropped.
 *
 * `viewId` is the saved view in force (`?view=`): set by `applyView`, dropped by any
 * later filter change, so an export names the view only while its filters are the ones
 * on screen (contract v1.3: exports carry `view_id` while a saved view is in force).
 */
export default function useClientFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => {
    const out = {};
    ARRAY_FILTERS.forEach((k) => { out[k] = params.getAll(k).filter(Boolean); });
    SCALAR_FILTERS.forEach((k) => { out[k] = params.get(k) || ""; });
    return out;
  }, [params]);

  const sort = useMemo(() => {
    const field = params.get("sort");
    const dir = params.get("dir");
    return SORT_FIELDS.includes(field) ? { field, dir: dir === "desc" ? "desc" : "asc" } : DEFAULT_SORT;
  }, [params]);

  const write = useCallback((mutate, { keepView = false } = {}) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (!keepView) next.delete("view");
    mutate(next);
    return next;
  }, { replace: true }), [setParams]);

  const setFilter = useCallback((key, value) => write((next) => {
    next.delete(key);
    if (Array.isArray(value)) value.filter(Boolean).forEach((v) => next.append(key, v));
    else if (value !== undefined && value !== null && value !== "") next.set(key, String(value));
  }), [write]);

  const setSort = useCallback((field) => write((next) => {
    const same = sort.field === field;
    next.set("sort", field);
    next.set("dir", same && sort.dir === "asc" ? "desc" : "asc");
  }, { keepView: true }), [write, sort]);

  const clear = useCallback(() => write((next) => {
    [...ARRAY_FILTERS, ...SCALAR_FILTERS].forEach((k) => next.delete(k));
  }), [write]);

  /** Applies a saved view: its filters replace the current ones, its sort too. */
  const applyView = useCallback((view) => write((next) => {
    [...ARRAY_FILTERS, ...SCALAR_FILTERS, "sort", "dir"].forEach((k) => next.delete(k));
    const f = view?.filters || {};
    ARRAY_FILTERS.forEach((k) => (Array.isArray(f[k]) ? f[k] : []).forEach((v) => next.append(k, v)));
    SCALAR_FILTERS.forEach((k) => { if (f[k] !== undefined && f[k] !== null && f[k] !== "") next.set(k, String(f[k])); });
    if (view?.sort?.field) { next.set("sort", view.sort.field); next.set("dir", view.sort.dir === "desc" ? "desc" : "asc"); }
    if (view?.id) next.set("view", view.id);
  }, { keepView: true }), [write]);

  /** The filters as the API reads them (and as a saved view stores them). */
  const apiFilters = useMemo(() => {
    const out = {};
    ARRAY_FILTERS.forEach((k) => { if (filters[k].length) out[k] = filters[k]; });
    SCALAR_FILTERS.forEach((k) => {
      const v = filters[k];
      if (!v) return;
      if (k === "q" && v.trim().length < SEARCH_MIN_CHARS) return;
      out[k] = k === "inactive_days" ? Number(v) : v;
    });
    return out;
  }, [filters]);

  const query = useMemo(() => ({ ...apiFilters, sort: sort.field, dir: sort.dir }), [apiFilters, sort]);
  const isFiltered = Object.keys(apiFilters).length > 0;

  const viewId = params.get("view") || null;
  return { filters, sort, setFilter, setSort, clear, applyView, apiFilters, query, isFiltered, viewId };
}
